import { AidaService, ILlmConversation, LlmConversation, LlmConversationService } from '@adaptiva/ai/common';
import { ElapsedTimePipe, isEmptyInput } from '@adaptiva/core/common';
import { ErrorHandlerService } from '@adaptiva/core/error-handler';
import { OverlayService } from '@adaptiva/core/overlays';
import { IListServerResult, IListServerService, ListServerSession, SchemaAndRows } from '@adaptiva/core/tables';
import { PositioningService } from '@adaptiva/core/toggles';
import { BaseListServerView, provideBaseWidget } from '@adaptiva/core/widgets';
import { IObjectTableListConfiguration, ObjectTableSupporterService } from '@adaptiva/data-service';
import { IconComponent } from '@adaptiva/widgets/icon';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, forwardRef, inject, model, ModelSignal, OnDestroy, Signal, signal, viewChild, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, of, switchMap } from 'rxjs';
import { ILlmConversationOverlayContext, LlmConversationOverlayComponent } from '../llm-conversations-overlay';

@Component({
	selector: 'ada-llm-switch',
	templateUrl: './llm-switch.component.html',
	styleUrl: './llm-switch.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		provideBaseWidget(() => LlmSwitchComponent),
		{
			provide: IListServerService,
			useExisting: forwardRef(() => ObjectTableSupporterService),
		},
	],
	imports: [
		CommonModule,
		ElapsedTimePipe,
		IconComponent,
	],
	host: {
		'[class.ada-full-width]': 'true',
		'[class.ada-display-block]': 'true',
	},
})
export class LlmSwitchComponent extends BaseListServerView<ILlmConversation, IObjectTableListConfiguration> implements AfterViewInit, OnDestroy {

	public override readonly sessionStarter: ModelSignal<ListServerSession.Initializer<ILlmConversation, IObjectTableListConfiguration>> = model(
		(_config: IObjectTableListConfiguration) => {
			return this.listService.createTable(_config).pipe(
				switchMap((_result: IListServerResult<ILlmConversation>) => {
					const _session: ListServerSession<ILlmConversation, IObjectTableListConfiguration> = new ListServerSession(_result, this.listService, _config);

					if (_result?.data == null) {
						return _session.navigate({ limit: _config.limit, offset: _config.offset }).pipe(
							map(() => _session)
						);
					}

					return of(_session);
				})
			);
		}
	);

	protected readonly activeButtonIndex: WritableSignal<int | null> = signal(null);

	protected readonly aida: AidaService = inject(AidaService);

	protected readonly llmConversations: LlmConversationService = inject(LlmConversationService);

	protected readonly dropdown: Signal<ElementRef<HTMLDivElement>> = viewChild('Dropdown');

	protected readonly dropdownPosition: WritableSignal<Record<string, string>> = signal({});

	protected readonly dropdownWidth: WritableSignal<int> = signal(0);

	protected readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

	protected readonly isDropdownOpen: WritableSignal<boolean> = signal(false);

	protected readonly overlays: OverlayService = inject(OverlayService);

	protected readonly positioning: PositioningService = inject(PositioningService);

	protected readonly resizeObserver: ResizeObserver = new ResizeObserver((e: ResizeObserverEntry[]) => this._resizeDropdown(e));

	protected readonly sessionData: Signal<SchemaAndRows<ILlmConversation>> = toSignal(this.data$);

	protected readonly conversations: Signal<ILlmConversation[]> = computed(() => {
		return this.sessionData()?.rows || [];
	});

	protected readonly dropdownStyle: Signal<Record<string, string>> = computed(() => ({
		...this.dropdownPosition(),
		width: `${this.dropdownWidth()}px`
	}));

	public override ngAfterViewInit(): void {
		this.resizeObserver.observe(this.mainElementSignal().nativeElement);
		super.ngAfterViewInit();

		if (!this.queryConfiguration())
			this.queryConfiguration.set(this.llmConversations.conversationQueryConfig());

		if (!this.session())
			this.startSession(this.queryConfiguration());
	}

	public override ngOnDestroy(): void {
		this.resizeObserver.disconnect();
		super.ngOnDestroy();
	}

	protected viewAllChats(): void {
		this.overlays.create<ILlmConversationOverlayContext>(
			LlmConversationOverlayComponent,
			{
				title: 'Select a Chat',
				queryConfig: {
					...this.llmConversations.conversationQueryConfig(),
					limit: 10
				},
			}
		).pipe(
			this.takeOneUntilDestroyed(),
		).subscribe({
			next: (_overlay: LlmConversationOverlayComponent) => {
				this.llmConversations.conversationOverlay.set(_overlay);
			}
		});
	}

	protected startConversation(_conversationId?: long): void {
		this.aida.startConversation(_conversationId).subscribe({
			error: (e: Error) => {
				this.errorHandler.defaultErrorHandler(e, 'LlmSwitchComponent.startConversation');
			}
		});
	}

	protected toggleDropdown(_isOpen: boolean): void {
		this.isDropdownOpen.set(_isOpen);

		if (_isOpen)
			this.openDropdown();
		else
			this.closeDropdown(true);
	}

	protected openDropdown(): void {
		this._setDropdownAttributes();
		this.dropdown()?.nativeElement.showPopover();
	}

	protected closeDropdown(_shouldClose: boolean): void {
		if (_shouldClose) {
			this.isDropdownOpen.set(false);
			this.dropdown()?.nativeElement.hidePopover();
		}
	}

	protected onRenameClick(_event: MouseEvent, _conversation: ILlmConversation): void {
		_event.stopImmediatePropagation();
		this.llmConversations.renameChat(_conversation).subscribe({
			next: (_response: boolean) => {
				this.closeDropdown(true);
			},
			error: (e: Error) => {
				this._widgetService.logging.defaultErrorHandler('LlmSwitchComponent.onRenameClick', e);
				this.closeDropdown(true);
			}
		});
	}

	protected onDeleteClick(_event: MouseEvent, _conversation: ILlmConversation): void {
		_event.stopImmediatePropagation();
		this.llmConversations.deleteChat(_conversation).subscribe({
			next: () => {
				this.closeDropdown(true);
			},
			error: (e: Error) => {
				this._widgetService.logging.defaultErrorHandler('LlmSwitchComponent.onDeleteClick', e);
				this.closeDropdown(true);
			}
		});
	}

	protected createNewConversation(): void {
		this.startConversation();
		this.closeDropdown(true);
	}

	protected selectConversation(_conversation: LlmConversation): void {
		this.aida.conversation.set(_conversation);
		this.closeDropdown(true);
	}

	private _resizeDropdown(_entries: ResizeObserverEntry[]): void {
		if (this.isDropdownOpen() && !isEmptyInput(_entries))
			this.dropdownWidth.set(_entries[0].contentRect.width);
	}

	private _setDropdownAttributes(): void {
		const _mainElement: HTMLElement = this.mainElementSignal().nativeElement;

		this.dropdownWidth.set(_mainElement.offsetWidth);
		this.dropdownPosition.set(
			this.positioning.alignVertically(
				_mainElement,
				{ verticalOffset: 3 },
			)
		);
	}

}
